if (!customElements.get('native-carousel')) {
    class NativeCarousel extends HTMLElement {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll("native-carousel-item"));
            this.pageDotsElements = Array.from(this.querySelectorAll("page-dots"));
            this.prevNextButtonsElements = Array.from(this.querySelectorAll("prev-next-buttons"));
            this.scrollerElement = this.items[0]?.parentElement;

            if (this.items.length > 1) {
                this.addEventListener("prev-next:prev", this.prev.bind(this));
                this.addEventListener("prev-next:next", this.next.bind(this));
                this.addEventListener("page-dots:changed", (event) => this.select(event.detail.index, true));

                if (Shopify.designMode) {
                    this.addEventListener("shopify:block:select", (event) =>
                        this.select(event.target.index, !event.detail.load)
                    );
                }
            }

            this._bindSwipeEvents();

            this.intersectionObserver = new IntersectionObserver(
                this._onVisibilityChanged.bind(this),
                {
                    root: this.scrollerElement,
                    rootMargin: `${this.scrollerElement.clientHeight}px 0px`,
                    threshold: 0.8,
                }
            );

            this.items.forEach((item) => this.intersectionObserver.observe(item));
        }

        disconnectedCallback() {
            if (this.intersectionObserver) {
                this.intersectionObserver.disconnect();
            }
        }

        get selectedIndex() {
            return this.items.findIndex((item) => item.selected);
        }

        prev(shouldAnimate = true) {
            this.select(Math.max(this.selectedIndex - 1, 0), shouldAnimate);
        }

        next(shouldAnimate = true) {
            this.select(Math.min(this.selectedIndex + 1, this.items.length - 1), shouldAnimate);
        }

        select(index, shouldAnimate = true) {
            const clampIndex = Math.max(0, Math.min(index, this.items.length - 1));
            const selectedElement = this.items[clampIndex];
            const direction = window.themeVariables?.settings?.direction === "rtl" ? -1 : 1;

            this._adjustNavigationForElement(selectedElement);

            if (shouldAnimate) {
                this.items.forEach((item) => this.intersectionObserver.unobserve(item));
                setTimeout(() => {
                    this.items.forEach((item) => this.intersectionObserver.observe(item));
                }, 800);
            }

            this.items.forEach((item, loopIndex) => (item.selected = loopIndex === clampIndex));

            if (selectedElement?.parentElement) {
                selectedElement.parentElement.scrollTo({
                    left: direction * (selectedElement.clientWidth * clampIndex),
                    behavior: shouldAnimate ? "smooth" : "auto",
                });
            }
        }

        _adjustNavigationForElement(selectedElement) {
            this.items.forEach((item) => (item.selected = selectedElement === item));

            this.pageDotsElements.forEach(
                (pageDot) => (pageDot.selectedIndex = selectedElement.index)
            );

            this.prevNextButtonsElements.forEach((prevNextButton) => {
                prevNextButton.isPrevDisabled = selectedElement.index === 0;
                prevNextButton.isNextDisabled = selectedElement.index === this.items.length - 1;
            });
        }

        _onVisibilityChanged(entries) {
            for (let entry of entries) {
                if (entry.isIntersecting) {
                    this._adjustNavigationForElement(entry.target);
                    break;
                }
            }
        }

        _bindSwipeEvents() {
            if (!this.scrollerElement) return;

            let startX = 0;
            let endX = 0;

            this.scrollerElement.addEventListener(
                "touchstart",
                (e) => {
                    startX = e.touches[0].clientX;
                },
                { passive: true }
            );

            this.scrollerElement.addEventListener(
                "touchmove",
                (e) => {
                    endX = e.touches[0].clientX;
                },
                { passive: true }
            );

            this.scrollerElement.addEventListener(
                "touchend",
                () => {
                    const diffX = endX - startX;
                    if (Math.abs(diffX) > 50) {
                        if (diffX > 0) {
                            this.prev(true);
                        } else {
                            this.next(true);
                        }
                    }
                },
                { passive: true }
            );
        }
    }

    customElements.define("native-carousel", NativeCarousel);
}

if (!customElements.get("native-carousel-item")) {
    class NativeCarouselItem extends HTMLElement {
        static get observedAttributes() {
            return ["hidden"];
        }
        get index() {
            return [...this.parentNode.children].indexOf(this);
        }
        get selected() {
            return !this.hasAttribute("hidden");
        }
        set selected(value) {
            this.hidden = !value;
        }
    }
    customElements.define("native-carousel-item", NativeCarouselItem);
}

if (!customElements.get("page-dots")) {
    class PageDots extends HTMLElement {
        connectedCallback() {
            this.buttons = Array.from(this.querySelectorAll("button"));
            this.buttons.forEach((button, index) => {
                button.addEventListener("click", () => {
                    this._dispatchEvent(index);
                });
            });
        }

        get selectedIndex() {
            return this.buttons.findIndex(
                (button) => button.getAttribute("aria-current") === "true"
            );
        }

        set selectedIndex(selectedIndex) {
            this.buttons.forEach((button, index) =>
                button.setAttribute("aria-current", selectedIndex === index ? "true" : "false")
            );

            // Center the selected dot if scrollable
            if (this.hasAttribute("align-selected")) {
                const selectedItem = this.buttons[selectedIndex];
                const windowHalfWidth = window.innerWidth / 2;
                const boundingRect = selectedItem.getBoundingClientRect();
                const scrollableElement = this._findFirstScrollableElement(this.parentElement);

                if (scrollableElement) {
                    scrollableElement.scrollTo({
                        behavior: "smooth",
                        left:
                            scrollableElement.scrollLeft +
                            (boundingRect.left - windowHalfWidth) +
                            boundingRect.width / 2,
                    });
                }
            }
        }

        _dispatchEvent(index) {
            if (index !== this.selectedIndex) {
                this.dispatchEvent(
                    new CustomEvent("page-dots:changed", {
                        bubbles: true,
                        detail: { index },
                    })
                );
            }
        }

        _findFirstScrollableElement(item, currentDepth = 0) {
            if (item === null || currentDepth > 3) {
                return null;
            }
            return item.scrollWidth > item.clientWidth
                ? item
                : this._findFirstScrollableElement(item.parentElement, currentDepth + 1);
        }
    }

    customElements.define("page-dots", PageDots);
}
